import { Avatar, AvatarFallback, AvatarImage } from '@talentmesh/ui/components/avatar';
import { Button } from '@talentmesh/ui/components/button';
import { Card, CardContent } from '@talentmesh/ui/components/card';
import { Spinner } from '@talentmesh/ui/components/spinner';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@talentmesh/ui/components/tooltip';
import { TypographyH4 } from '@talentmesh/ui/components/typography';
import {
    AtsConnectionResponse,
    AtsIntegrationTool,
    AtsSetupStatus,
    useDeleteAtsConnection,
} from '../api/useAtsIntegration';
import StatusInfo from '../EmailIntegrations/StatusInfo';
import { useNotificationContext } from '@/Context/NotificationContext';
import toDateString from '@/Utils/DateHelper';
import UIStrings from '@/Utils/UIStrings';

interface AtsIntegrationStatusProps {
    connection: AtsConnectionResponse;
    /** Catalog entry for the connected tool - supplies its display name and logo. */
    tool?: AtsIntegrationTool;
    canManage: boolean;
    onDisconnected: () => Promise<unknown> | void;
}

interface SetupStatusPresentation {
    label: string;
    dotClassName: string;
}

const setupStatusPresentations: Record<AtsSetupStatus, SetupStatusPresentation> = {
    Completed: { label: UIStrings.Connected, dotClassName: 'bg-green-500' },
    FinalSyncPending: { label: UIStrings.AtsSetupFinalSyncPending, dotClassName: 'bg-amber-500' },
    Incomplete: { label: UIStrings.AtsSetupIncomplete, dotClassName: 'bg-amber-500' },
    Unknown: { label: UIStrings.AtsSetupUnknown, dotClassName: 'bg-muted-foreground' },
};

const AtsIntegrationStatus = ({ connection, tool, canManage, onDisconnected }: AtsIntegrationStatusProps) => {
    const { showSuccessToaster, showFailToaster } = useNotificationContext();
    const { trigger: deleteConnection, isMutating: isDeleting } = useDeleteAtsConnection();

    // The tool catalog is the source of truth for how a tool is presented; the connection only stores
    // the slug, so fall back to it if the catalog no longer lists the connected tool.
    const label = tool?.label ?? connection.atsTool;
    const logoUrl = tool?.assets.iconUrl ?? tool?.assets.logoUrl;
    // The lookup can miss: a backend newer than this build could report a status the union does not
    // list, and indexing it would otherwise crash the card rather than degrade the label.
    const status = setupStatusPresentations[connection.setupStatus] ?? setupStatusPresentations.Unknown;
    const isSyncing = connection.setupStatus === 'FinalSyncPending';

    const handleDisconnect = async () => {
        try {
            await deleteConnection();
            await onDisconnected();
            showSuccessToaster(UIStrings.AtsDisconnected);
        } catch {
            // Revoking at the ATS provider can fail, in which case the connection is deliberately left
            // in place server-side - so the card stays and the recruiter can retry.
            showFailToaster();
        }
    };

    return (
        <Card className="w-max rounded-xl border p-4 drop-shadow-none">
            <CardContent className="flex flex-col gap-6 p-0" data-cy="AtsIntegrationDetails">
                <div className="flex flex-row items-center gap-2">
                    <Avatar className="size-10 rounded-full">
                        {logoUrl ? <AvatarImage src={logoUrl} alt={`${label} logo`} /> : null}
                        <AvatarFallback className="rounded-full">{label.slice(0, 2)}</AvatarFallback>
                    </Avatar>
                    <TypographyH4 className="text-lg capitalize">{label}</TypographyH4>
                </div>
                <div className="grid w-auto grid-cols-12 gap-x-8 gap-y-2">
                    <StatusInfo label={UIStrings.Status}>
                        {isSyncing ? (
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger
                                        className="inline-flex cursor-help items-center gap-1"
                                        data-cy="AtsSyncStatus"
                                    >
                                        <Spinner className="size-3" />
                                        {status.label}
                                    </TooltipTrigger>
                                    <TooltipContent className="whitespace-normal">
                                        {UIStrings.AtsSyncInProgressSettingsHint}
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        ) : (
                            <>
                                <span
                                    className={`mr-1 inline-block h-2 w-2 rounded-full ${status.dotClassName}`}
                                    aria-hidden
                                />
                                {` ${status.label}`}
                            </>
                        )}
                    </StatusInfo>
                    <StatusInfo label={UIStrings.ConnectedOn}>
                        {connection.connectedAt ? toDateString(new Date(connection.connectedAt)) : ''}
                    </StatusInfo>
                </div>
                {canManage && (
                    <div className="flex flex-row gap-2">
                        <Button
                            type="button"
                            variant="destructive-outline"
                            size="sm"
                            disabled={isDeleting}
                            onClick={handleDisconnect}
                            className="flex-1"
                        >
                            {isDeleting && <Spinner className="mr-2 size-4" />}
                            {UIStrings.Disconnect}
                        </Button>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

export default AtsIntegrationStatus;
